import type { MfaVerifyOutput } from '@navio/nsdk';
import { URLKeys, noddiAsync } from 'noddi-async';
import { NoddiButton, NoddiOtp, useAppForm } from 'noddi-ui-web';
import { useRef, useState } from 'react';
import { z } from 'zod';

const OTP_LENGTH = 6;

interface MfaChallengeTranslations {
  title: string;
  description: string;
  code: string;
  verify: string;
  back: string;
  invalidCode: string;
  recoveryDescription: string;
  recoveryCode: string;
  useRecoveryCode: string;
  useAuthenticator: string;
}

interface MfaChallengeStepProps {
  challengeToken: string;
  onVerified: (data: MfaVerifyOutput) => void;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  onBack: () => void;
  translations?: MfaChallengeTranslations;
}

const validationSchema = z.object({
  code: z.string().min(1, 'Code is required')
});

type FormValues = z.input<typeof validationSchema>;

/**
 * Second-factor step shown after a successful password login when the user
 * has MFA enabled. Exchanges the short-lived challenge token plus a TOTP
 * (or recovery) code for a real session token via postLoginMfaVerify.
 */
export const MfaChallengeStep = ({
  challengeToken,
  onVerified,
  setErrorMessage,
  onBack,
  translations
}: MfaChallengeStepProps) => {
  const texts = translations ?? {
    title: 'Two-factor authentication',
    description: 'Enter the 6-digit code from your authenticator app.',
    code: 'Authentication code',
    verify: 'Verify',
    back: 'Back',
    invalidCode: 'Invalid code. Please try again.',
    recoveryDescription: 'Enter one of your recovery codes.',
    recoveryCode: 'Recovery code',
    useRecoveryCode: 'Use a recovery code',
    useAuthenticator: 'Use authenticator app'
  };

  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const { mutateAsync: verify } = noddiAsync.usePost({
    type: URLKeys.postLoginMfaVerify,
    queryConfig: {
      onSuccess: (res) => {
        setErrorMessage('');
        onVerified(res.data);
      },
      onError: () => setErrorMessage(texts.invalidCode)
    }
  });

  const form = useAppForm({
    defaultValues: { code: '' } satisfies FormValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      setErrorMessage('');
      await verify({ challengeToken, code: value.code });
    }
  });

  return (
    <form.AppForm>
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1'>
            <p className='font-semibold'>{texts.title}</p>
            <p className='text-sm'>{useRecoveryCode ? texts.recoveryDescription : texts.description}</p>
          </div>
          <form.AppField name='code'>
            {(field) =>
              useRecoveryCode ? (
                // oxlint-disable-next-line jsx-a11y/no-autofocus -- the challenge step exists only to take this code
                <field.Input autoFocus label={texts.recoveryCode} autoComplete='one-time-code' />
              ) : (
                <div className='flex flex-col gap-1'>
                  <span className='text-sm font-medium'>{texts.code}</span>
                  <NoddiOtp
                    value={field.state.value}
                    onChange={field.handleChange}
                    onComplete={() => {
                      formRef.current?.requestSubmit();
                    }}
                    maxLength={OTP_LENGTH}
                  />
                </div>
              )
            }
          </form.AppField>
          <form.SubscribeButton label={texts.verify} className='w-full' />
          <NoddiButton
            type='button'
            variant='link'
            size='small'
            onClick={() => {
              setUseRecoveryCode((prev) => !prev);
              setErrorMessage('');
              form.setFieldValue('code', '');
            }}
          >
            {useRecoveryCode ? texts.useAuthenticator : texts.useRecoveryCode}
          </NoddiButton>
          <NoddiButton type='button' variant='link' size='small' onClick={onBack}>
            {texts.back}
          </NoddiButton>
        </div>
      </form>
    </form.AppForm>
  );
};
